import { useState } from "react"
import type { ComponentProps, SubmitEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import UserActiveContextFields from "@/v2-backoffice-client/user/update/UserActiveContextFields"
import {
  type UserActiveContextValues,
  useUserActiveContext,
} from "@/v2-backoffice-client/user/update/user.update.hooks"
import {
  getUserActiveLanguageCode,
  USER_LANGUAGE_OPTIONS,
} from "@/v2-backoffice-client/user/user.constants"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type UserUpdateFormData = {
  FirstName: string
  LastName: string
  Email: string
  // Only present when actually changed: an untouched dialog must not stamp
  // a language onto a user who never chose one.
  Language?: string
  // The active team/project/country write, or null when there is nothing
  // to write (no team chosen, or nothing changed).
  active: UserActiveContextValues | null
}

export type UserUpdateFormProps = Omit<ComponentProps<"form">, "onSubmit"> & {
  user: UserFragment
  onSubmit(data: UserUpdateFormData, e: SubmitEvent<HTMLFormElement>): void
}

// The user update fields: identity, language, and the active-context group.
// Mounted inside Dialog.Content, so every opening is a fresh mount — the
// plain useState initializers below are the per-open seeding, and a
// cancelled edit is discarded with the unmount. The customer that owns the
// user is not a field here: like a team's customer, it has its own action
// (Customer in the toolbar, Update Customer in the menus), with the guard
// that judges the pick against the user's teams.
function UserUpdateForm(props: UserUpdateFormProps) {
  const { user, onSubmit, className, ...formProps } = props

  // "" = the user has no stored language; the app then applies its default.
  const initialLanguage = getUserActiveLanguageCode(user.Configuration)
  const [firstName, setFirstName] = useState(user.FirstName ?? "")
  const [lastName, setLastName] = useState(user.LastName ?? "")
  const [email, setEmail] = useState(user.Email ?? "")
  const [language, setLanguage] = useState(initialLanguage)

  // Active team/project/country live in the same dialog as language (all
  // four are Configuration-backed support knobs), but persist through a
  // separate mutation — the hook decides whether there is anything to write.
  const active = useUserActiveContext(user)

  return (
    <form
      className={cn("space-y-4 py-4", className)}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(
          {
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            Language: language !== initialLanguage ? language : undefined,
            active: active.values,
          },
          e,
        )
      }}
      {...formProps}
    >
      <div className="space-y-2">
        <Label htmlFor="editFirstName">First Name</Label>
        <Input
          id="editFirstName"
          type="text"
          autoFocus
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value)
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="editLastName">Last Name</Label>
        <Input
          id="editLastName"
          type="text"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value)
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="editEmail">Email</Label>
        <Input
          id="editEmail"
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="editLanguage">Language</Label>
        <Select value={language || undefined} onValueChange={setLanguage}>
          <Select.Trigger
            id="editLanguage"
            size="md"
            className="w-full"
            title="The user's app language (what the console renders in). Applies from their next session refresh."
          >
            <Select.Value placeholder="Not set — app default" />
          </Select.Trigger>
          <Select.Content>
            {USER_LANGUAGE_OPTIONS.map((opt) => (
              <Select.Item key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>

      <UserActiveContextFields context={active} />
    </form>
  )
}

export default UserUpdateForm
